import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import {
  type FreightClass,
  HandlingUnitTypeLabels,
} from "@redotech/redo-fulfillment-carrier-common/freight";
import type { FulfillmentOrderAddress } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import type { FreightQuote } from "@redotech/redo-fulfillment-operations-common/freight/freight-quote-request";
import { convertWeight } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import GlobeIcon from "@redotech/redo-icons/globe-02.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState } from "react";
import { RedoMerchantRpcClientContext } from "../rpc-client";
import type {
  FreightBookingState,
  FreightBookingStep,
} from "./freight-booking-types";
import { QuoteCard } from "./quote-card";
import { QuotesLoading } from "./quotes-loading";

interface QuotesStepProps {
  state: FreightBookingState;
  originAddress: FulfillmentOrderAddress;
  destinationAddress: FulfillmentOrderAddress;
  selectedQuoteId: string | undefined;
  onSelectQuote(quote: FreightQuote | undefined): void;
  onGoToStep(step: FreightBookingStep): void;
}

export const QuotesStep = memo(function QuotesStep({
  state,
  originAddress,
  destinationAddress,
  selectedQuoteId,
  onSelectQuote,
  onGoToStep,
}: QuotesStepProps) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const [showResults, setShowResults] = useState(false);

  const quotesLoad = useLoad(async () => {
    onSelectQuote(undefined);
    const productsById = new Map(state.freight.products.map((p) => [p.id, p]));
    const handlingUnitList = state.freight.handlingUnits
      .filter((unit) => unit.assignedProducts.length > 0)
      .map((unit) => {
        if (!unit.freightClass) {
          throw new Error("Freight class not set for handling unit");
        }
        const shippedItems = unit.assignedProducts.map((ap) => {
          const product = productsById.get(ap.productId);
          if (!product) {
            throw new Error(
              `Product not found for assigned product ${ap.productId}`,
            );
          }

          const weightLbs =
            convertWeight(product.itemWeight, WeightUnit.Lb).value *
            ap.quantity;
          return {
            description: product.name,
            quantity: ap.quantity,
            weightLbs,
            dimensions: product.dimensions,
          };
        });

        const totalWeightLbs = shippedItems.reduce(
          (sum, item) => sum + item.weightLbs,
          0,
        );

        return {
          type: unit.type,
          quantity: unit.quantity,
          stackable: unit.stackable,
          freightClass: unit.freightClass!,
          totalWeightLbs,
          dimensions: unit.dimensions,
          shippedItems,
        };
      });

    return rpcClient.generateFreightQuotes({
      originAddress,
      destinationAddress,
      pickupDateTime: state.route.pickupDateTime!.toString(),
      pickupLocationType: state.route.pickupLocationType,
      deliveryLocationType: state.route.deliveryLocationType,
      pickupAccessorials: {
        ...state.route.pickupAccessorials,
        instructions: state.route.pickupAccessorials.instructions || undefined,
      },
      deliveryAccessorials: {
        ...state.route.deliveryAccessorials,
        instructions:
          state.route.deliveryAccessorials.instructions || undefined,
      },
      handlingUnitList,
      customerReference1: state.freight.customerReference1 || undefined,
      customerReference2: state.freight.customerReference2 || undefined,
      specialInstructions: state.freight.specialInstructions || undefined,
    });
  }, []);

  const handleAnimationComplete = useCallback(() => {
    setShowResults(true);
  }, []);

  const handleGoToFreight = useCallback(() => {
    onGoToStep("freight");
  }, [onGoToStep]);

  const routeSummary = useMemo(() => {
    const originCity = [originAddress.city, originAddress.province]
      .filter(Boolean)
      .join(", ");
    const destCity = [destinationAddress.city, destinationAddress.province]
      .filter(Boolean)
      .join(", ");

    const totalUnits = state.freight.handlingUnits.reduce(
      (sum, u) => sum + u.quantity,
      0,
    );
    const unitType =
      state.freight.handlingUnits.length > 0
        ? HandlingUnitTypeLabels[state.freight.handlingUnits[0]!.type]
        : "unit";
    const unitLabel =
      totalUnits === 1 ? unitType.toLowerCase() : `${unitType.toLowerCase()}s`;

    const totalWeightLbs = state.freight.handlingUnits.reduce((sum, unit) => {
      const productsById = new Map(
        state.freight.products.map((p) => [p.id, p]),
      );
      return (
        sum +
        unit.assignedProducts.reduce((unitSum, ap) => {
          const product = productsById.get(ap.productId);
          return (
            unitSum +
            (product
              ? convertWeight(product.itemWeight, WeightUnit.Lb).value *
                ap.quantity
              : 0)
          );
        }, 0)
      );
    }, 0);

    const freightClasses = [
      ...new Set(
        state.freight.handlingUnits
          .map((u) => u.freightClass)
          .filter((c): c is FreightClass => c !== undefined),
      ),
    ].sort((a, b) => Number(a) - Number(b));

    const parts = [`LTL - ${totalUnits} ${unitLabel}`];
    if (totalWeightLbs > 0) {
      parts.push(`${Math.round(totalWeightLbs).toLocaleString()} lbs`);
    }
    if (freightClasses.length > 0) {
      parts.push(
        `Class${freightClasses.length > 1 ? "es" : ""} ${freightClasses.join(", ")}`,
      );
    }

    return { originCity, destCity, summary: parts.join(" \u2022 ") };
  }, [originAddress, destinationAddress, state.freight]);

  if (quotesLoad.pending || !showResults) {
    return (
      <QuotesLoading
        onAnimationComplete={handleAnimationComplete}
        pending={quotesLoad.pending}
      />
    );
  }

  if (quotesLoad.error) {
    return (
      <Flex dir="column" flex={1}>
        <RouteSummaryHeader
          destCity={routeSummary.destCity}
          onEditFreight={handleGoToFreight}
          originCity={routeSummary.originCity}
          summary={routeSummary.summary}
        />
        <Flex
          align="center"
          dir="column"
          flex={1}
          gap="lg"
          justify="center"
          p="3xl"
        >
          <RedoFeaturedIcon
            color="brand"
            Icon={GlobeIcon}
            shape="circle"
            size="lg"
          />
          <Flex align="center" dir="column" gap="xs">
            <Text fontSize="md" fontWeight="semibold">
              Could not find quotes
            </Text>
            <Text
              fontSize="sm"
              style={{ maxWidth: 360, textAlign: "center" }}
              textColor="secondary"
            >
              There was an error fetching quotes. Double check your freight
              details to ensure they are correct.
            </Text>
          </Flex>
          <RedoButton
            hierarchy="primary"
            onClick={handleGoToFreight}
            size="sm"
            text="Back to freight details"
          />
        </Flex>
      </Flex>
    );
  }

  const quotes: FreightQuote[] = quotesLoad.value ?? [];

  if (quotes.length === 0) {
    return (
      <Flex dir="column" flex={1}>
        <RouteSummaryHeader
          destCity={routeSummary.destCity}
          onEditFreight={handleGoToFreight}
          originCity={routeSummary.originCity}
          summary={routeSummary.summary}
        />
        <Flex
          align="center"
          dir="column"
          flex={1}
          gap="lg"
          justify="center"
          p="3xl"
        >
          <RedoFeaturedIcon
            color="brand"
            Icon={GlobeIcon}
            shape="circle"
            size="lg"
          />
          <Flex align="center" dir="column" gap="xs">
            <Text fontSize="md" fontWeight="semibold">
              Could not find quotes
            </Text>
            <Text
              fontSize="sm"
              style={{ maxWidth: 360, textAlign: "center" }}
              textColor="secondary"
            >
              No carriers returned quotes for this shipment. Double check your
              freight details to ensure they are correct.
            </Text>
          </Flex>
          <RedoButton
            hierarchy="primary"
            onClick={handleGoToFreight}
            size="sm"
            text="Back to freight details"
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex dir="column" flex={1}>
      <RouteSummaryHeader
        destCity={routeSummary.destCity}
        onEditFreight={handleGoToFreight}
        originCity={routeSummary.originCity}
        summary={routeSummary.summary}
      />
      <Flex
        dir="column"
        gap="xl"
        mx="auto"
        p="3xl"
        style={{ maxWidth: 880 }}
        w="full"
      >
        <Text fontSize="sm" fontWeight="semibold">
          {quotes.length} {quotes.length === 1 ? "quote" : "quotes"} available
        </Text>
        <Flex dir="column" gap="md">
          {quotes.map((quote) => (
            <QuoteCard
              key={quote.quoteId}
              onSelect={() => onSelectQuote(quote)}
              quote={quote}
              selected={selectedQuoteId === quote.quoteId}
            />
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
});

interface RouteSummaryHeaderProps {
  originCity: string;
  destCity: string;
  summary: string;
  onEditFreight(): void;
}

const RouteSummaryHeader = memo(function RouteSummaryHeader({
  originCity,
  destCity,
  summary,
  onEditFreight,
}: RouteSummaryHeaderProps) {
  return (
    <Flex
      align="center"
      borderBottomWidth="1px"
      borderColor="primary"
      justify="space-between"
      px="3xl"
      py="lg"
      style={{ backgroundColor: "var(--redo-colors-background-bg-primary)" }}
    >
      <Text fontSize="sm" fontWeight="semibold">
        {originCity}
        {" \u2192 "}
        {destCity}
      </Text>
      <Text fontSize="xs" textColor="secondary">
        {summary}
      </Text>
      <RedoButton
        hierarchy="link"
        onClick={onEditFreight}
        size="sm"
        text="Edit freight"
      />
    </Flex>
  );
});
